"use client";

import { useApolloClient } from "@apollo/client/react";
import { useCallback, useState } from "react";
import {
  useUpdateVisitorProfileMutation,
  type VisitorProfile,
  VisitorProfileDocument,
} from "@/lib/graphql/queries/generated/profile.generated";
import { useOutboxContext } from "@/lib/outbox-context";
import { setRuntimeLocale } from "@/lib/runtime-locale";
import { extractProfileErrorKind, profileErrorI18nKey } from "./profile-error";

export type PrefKey = "language" | "desktopNotifications" | "themeMode";
export type PrefValue = string | boolean | null;

/**
 * Auto-saves a single visitor preference with an Apollo optimistic
 * response so the widget chrome (theme, language) flips THIS tick;
 * the mutation completes in the background and the authoritative
 * reply replaces the optimistic one. On error Apollo rolls the cache
 * back automatically and the returned `error` surfaces an inline
 * i18n message for the caller to display.
 *
 * Demo mode (`onDemoApply` provided) — the GraphQL mutation is SKIPPED
 * entirely; the caller's local-state setter is called with the new
 * value so the UI still flips, and the runtime-locale bus is still
 * fired for language changes. No network round-trip, no Apollo cache
 * write — there's no real BE here.
 *
 * Returns:
 *   - `apply(key, value)` — fire-and-await the auto-save
 *   - `saving` — the key currently being saved (or `null`)
 *   - `error` — the most recent i18n error key, or `null`
 *   - `clearError` — wipe the error state (e.g. when the visitor
 *     interacts with another control)
 */
export function useApplyPref(
  profile: VisitorProfile | undefined,
  t: (key: string) => string,
  onDemoApply?: (key: PrefKey, value: PrefValue) => void,
) {
  const [update] = useUpdateVisitorProfileMutation();
  const outbox = useOutboxContext();
  const client = useApolloClient();
  const [saving, setSaving] = useState<PrefKey | null>(null);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const apply = useCallback(
    async (key: PrefKey, value: PrefValue) => {
      if (!profile) return;
      // Demo mode — short-circuit the network mutation. Just route the
      // change through the caller's local-state setter so the UI
      // reflects it, and fire the runtime-locale bus for language
      // switches (same chrome flip the real mutation triggers).
      if (onDemoApply) {
        onDemoApply(key, value);
        if (key === "language" && typeof value === "string") {
          setRuntimeLocale(value);
        }
        return;
      }
      setSaving(key);
      setError(null);

      const patch: Record<string, PrefValue> = { [key]: value };
      const optimisticPrefs = {
        __typename: "VisitorProfilePreferences" as const,
        ...profile.preferences,
        ...patch,
      };

      // Offline outbox path — apply the change optimistically to the Apollo
      // cache (so the chrome flips THIS tick, online or off) and queue a
      // CONVERGENCE op that syncs it to the BE + retries on reconnect. The op
      // carries the FULL preferences (not just this key) so a second change
      // superseding it (same convergenceKey) doesn't drop the first.
      if (outbox) {
        client.writeQuery({
          query: VisitorProfileDocument,
          data: { visitorProfile: { ...profile, preferences: optimisticPrefs } },
        });
        if (key === "language" && typeof value === "string") setRuntimeLocale(value);
        const { __typename, ...prefsInput } = optimisticPrefs;
        outbox.enqueue({
          op: "setPreferences",
          semantics: "convergence",
          convergenceKey: "preferences",
          payload: prefsInput,
        });
        setSaving(null);
        return;
      }

      try {
        await update({
          variables: { input: { preferences: patch } },
          // Apollo 3 needs `__typename: "Mutation"` on the root and a
          // typename on every nested object for the optimistic response
          // to merge into the cache (otherwise it silently drops the
          // optimistic write and the UI doesn't flip until the real
          // reply lands).
          optimisticResponse: {
            __typename: "Mutation" as const,
            updateVisitorProfile: {
              __typename: "VisitorProfile" as const,
              ...profile,
              preferences: optimisticPrefs,
            },
          },
          // Apollo can't auto-link `Mutation.updateVisitorProfile` with
          // `Query.visitorProfile` without a shared normalised id, and
          // `VisitorProfile` is intentionally id-less on the BE — so we
          // patch the query cache explicitly so the prefs hook + this
          // tab's reads see the change immediately.
          update: (cache, { data: mut }) => {
            const next = mut?.updateVisitorProfile;
            if (!next) return;
            cache.writeQuery({
              query: VisitorProfileDocument,
              data: { visitorProfile: next },
            });
          },
        });
        // Locale lives outside the Apollo graph — the runtime-locale
        // bus is what tells `RuntimeIntlProvider` to swap bundles.
        // (The prefs hook also fires this, but firing here too cuts a
        // tick of perceived latency on the actual click.)
        if (key === "language" && typeof value === "string") {
          setRuntimeLocale(value);
        }
      } catch (err) {
        const kind = extractProfileErrorKind(err);
        setError(t(profileErrorI18nKey(kind)));
      } finally {
        setSaving(null);
      }
    },
    [profile, t, update, onDemoApply, outbox, client],
  );

  return { apply, saving, error, clearError };
}
